// P6-B1 — storage signed-URL hot-spot fix (2026-05-06).
//
// The `asset_files` Supabase Storage bucket is PRIVATE
// (`storage.buckets.public = false` per migration 20260114025212).
// Pre-B1 this component called `supabase.storage.from('asset_files')
// .getPublicUrl(storagePath)` for both grid thumbnails and the preview
// dialog — producing URLs that aren't directly fetchable on a private
// bucket. The sibling `AssetDocumentsTab.tsx:128` already uses the
// correct `createSignedUrl(path, 3600)` pattern; this fix mirrors the
// canonical batched `createSignedUrls(paths, 3600)` shape from
// `useDviMedia.ts:47-49` because `<img src=...>` rendering needs URLs
// resolved up-front (the on-click async pattern in `AssetDocumentsTab`
// fits download surfaces, not inline image grids).
//
// The 3600-second expiry matches the canonical batch-signed-URL pattern
// (useDviMedia, useToolbox, artifactUpload, parking-lot views).
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useOptiAssets } from '@/hooks/useOptiAssets';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Camera, Upload, Trash2, Loader2, Image as ImageIcon, X } from 'lucide-react';
import { toast } from 'sonner';

type PhotoKind = 'overview' | 'serial_plate' | 'damage' | 'invoice';

const PHOTO_KIND_LABELS: Record<PhotoKind, string> = {
  overview: 'Overview',
  serial_plate: 'Serial/Plate',
  damage: 'Damage',
  invoice: 'Invoice/Receipt',
};

interface AssetPhoto {
  id: string;
  asset_id: string;
  storage_path: string;
  photo_kind: PhotoKind;
  caption: string | null;
  created_at: string;
  created_by: string | null;
}

interface AssetPhotosTabProps {
  assetId: string;
}

export function AssetPhotosTab({ assetId }: AssetPhotosTabProps) {
  const { activeShop } = useAuth();
  const { canEdit } = useOptiAssets();
  const queryClient = useQueryClient();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [photoKind, setPhotoKind] = useState<PhotoKind>('overview');
  const [caption, setCaption] = useState('');
  const [previewPhoto, setPreviewPhoto] = useState<AssetPhoto | null>(null);

  const { data: photos = [], isLoading } = useQuery({
    queryKey: ['asset-photos', assetId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('shop_asset_photos')
        .select('*')
        .eq('asset_id', assetId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as AssetPhoto[];
    },
    enabled: !!assetId,
  });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!selectedFile || !activeShop) throw new Error('No file selected');

      const fileExt = selectedFile.name.split('.').pop();
      const fileName = `${activeShop.id}/${assetId}/photos/${crypto.randomUUID()}.${fileExt}`;

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from('asset_files')
        .upload(fileName, selectedFile);
      if (uploadError) throw uploadError;

      // Create photo record
      const { error: insertError } = await supabase
        .from('shop_asset_photos')
        .insert({
          asset_id: assetId,
          shop_id: activeShop.id,
          storage_path: fileName,
          photo_kind: photoKind,
          caption: caption || null,
        });
      if (insertError) throw insertError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-photos', assetId] });
      setUploadDialogOpen(false);
      setSelectedFile(null);
      setCaption('');
      setPhotoKind('overview');
      toast.success('Photo uploaded');
    },
    onError: (error) => {
      toast.error('Failed to upload photo: ' + error.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (photo: AssetPhoto) => {
      // Delete from storage
      await supabase.storage.from('asset_files').remove([photo.storage_path]);
      // Delete record
      const { error } = await supabase
        .from('shop_asset_photos')
        .delete()
        .eq('id', photo.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-photos', assetId] });
      setPreviewPhoto(null);
      toast.success('Photo deleted');
    },
    onError: (error) => {
      toast.error('Failed to delete photo: ' + error.message);
    },
  });

  // P6-B1: batch-resolve signed URLs for every asset photo in one
  // round-trip via `createSignedUrls`. Keyed off assetId + the actual
  // storage paths so the cache hits across renders that don't change
  // the underlying photo list. While URLs are loading, `<img src={undefined}>`
  // renders nothing (matches pre-B1 broken-thumbnail state, so loading
  // UX is unchanged). 3600-second expiry mirrors the canonical batch-
  // signed-URL pattern from useDviMedia / useToolbox.
  const storagePaths = useMemo(
    () => photos.map((photo) => photo.storage_path),
    [photos],
  );
  const { data: signedUrlMap } = useQuery({
    queryKey: ['asset-photos-signed-urls', assetId, storagePaths],
    queryFn: async () => {
      if (storagePaths.length === 0) return new Map<string, string>();
      const { data, error } = await supabase.storage
        .from('asset_files')
        .createSignedUrls(storagePaths, 3600);
      if (error) {
        console.warn('[AssetPhotosTab] createSignedUrls failed:', error);
        return new Map<string, string>();
      }
      const map = new Map<string, string>();
      (data ?? []).forEach((entry) => {
        if (entry.signedUrl && entry.path) {
          map.set(entry.path, entry.signedUrl);
        }
      });
      return map;
    },
    enabled: storagePaths.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const getPhotoUrl = (storagePath: string) => signedUrlMap?.get(storagePath);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground">
          {photos.length} photo{photos.length !== 1 ? 's' : ''}
        </h3>
        {canEdit && (
          <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-2">
                <Camera className="h-4 w-4" />
                Add Photo
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Upload Photo</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Photo Type</Label>
                  <Select value={photoKind} onValueChange={(v) => setPhotoKind(v as PhotoKind)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(PHOTO_KIND_LABELS).map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Image File</Label>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Caption (optional)</Label>
                  <Input
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    placeholder="Brief description..."
                  />
                </div>
                <Button
                  onClick={() => uploadMutation.mutate()}
                  disabled={!selectedFile || uploadMutation.isPending}
                  className="w-full"
                >
                  {uploadMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  Upload
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {photos.length === 0 ? (
        <Card className="p-8 text-center">
          <ImageIcon className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
          <p className="text-sm text-muted-foreground">No photos uploaded yet</p>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {photos.map((photo) => (
            <Card
              key={photo.id}
              className="relative overflow-hidden cursor-pointer group"
              onClick={() => setPreviewPhoto(photo)}
            >
              <img
                src={getPhotoUrl(photo.storage_path)}
                alt={photo.caption || 'Asset photo'}
                className="w-full h-32 object-cover"
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <span className="text-white text-sm">View</span>
              </div>
              <Badge variant="secondary" className="absolute top-2 left-2 text-xs">
                {PHOTO_KIND_LABELS[photo.photo_kind]}
              </Badge>
            </Card>
          ))}
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={!!previewPhoto} onOpenChange={() => setPreviewPhoto(null)}>
        <DialogContent className="max-w-2xl">
          {previewPhoto && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between">
                  <span>{PHOTO_KIND_LABELS[previewPhoto.photo_kind]}</span>
                  {canEdit && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => deleteMutation.mutate(previewPhoto)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  )}
                </DialogTitle>
              </DialogHeader>
              <img
                src={getPhotoUrl(previewPhoto.storage_path)}
                alt={previewPhoto.caption || 'Asset photo'}
                className="w-full rounded-lg"
              />
              {previewPhoto.caption && (
                <p className="text-sm text-muted-foreground">{previewPhoto.caption}</p>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
